import { Controller } from "@hotwired/stimulus"

// Reveals a manually-overridden bound element's "why" reason on hover of the
// element/row ITSELF (card #2324 follow-up, Jay live-tested) -- not just the
// tiny lock-badge icon beside it (deck_override_lock_badge / _lock_icon still
// carry that as a keyboard/screen-reader `title`/sr-only fallback). Attached
// directly on the bound element or Measures-tab value (DecksHelper#deck_
// element_edit_attrs / decks/_measures_tab.html.erb), one controller instance
// per overridden element/row, reading its message straight off the `message`
// Value rather than a shared/looked-up dataset.
//
// TIMING mirrors the established hovercard pattern (hovercard_controller.js /
// place_owner_hovercard_controller.js): a short delay before showing, so a
// glancing mouse pass doesn't flash a popup, and before hiding, so moving the
// mouse INTO the popup itself doesn't instantly close it.
//
// PORTAL, not a pre-rendered hidden sibling (the usual hovercard shape): a
// canvas element always sits inside .airdrop-slide-scale > .airdrop-slide,
// which carries its OWN permanent CSS `transform: scale(...)` (fit-to-
// viewport sizing -- see application.css) -- and a `transform` on any
// ancestor makes THAT ancestor the containing block for a `position: fixed`
// descendant, not the real viewport (CSS spec). A pre-rendered `position:
// fixed` sibling living inside that transformed slide would size/position
// itself against the SLIDE's box, not the screen -- the exact double-scaling
// trap deck_show_editor_controller.js's own header comment already
// documents for this canvas. Appending the popup straight onto document.body
// on hover sidesteps this entirely: nothing between it and the real viewport
// carries a transform, so plain `position: fixed` + getBoundingClientRect()
// coordinates (already real screen pixels) line up correctly regardless of
// how far the slide is currently scaled down. The Measures tab (a plain,
// untransformed table) has no such problem, but reuses the identical
// controller rather than a second implementation.
export default class extends Controller {
  static values = { message: String }

  connect() {
    this._card = null
    this._showTimeout = null
    this._hideTimeout = null
  }

  disconnect() {
    this._clearTimeouts()
    this._removeCard()
  }

  show() {
    if (!this.messageValue) return

    this._clearTimeouts()
    this._showTimeout = setTimeout(() => this._showCard(), 200)
  }

  hide() {
    this._clearTimeouts()
    this._hideTimeout = setTimeout(() => this._removeCard(), 150)
  }

  _showCard() {
    if (this._card) return

    const card = document.createElement("div")
    card.textContent = this.messageValue
    // The "deck-override-reason-hovercard" marker class is a stable test
    // hook (no other on-page element carries it) -- everything else is
    // presentation only.
    card.className =
      "deck-override-reason-hovercard fixed z-50 max-w-xs rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-lg"
    // Hovering INTO the popup (e.g. to select/copy the text) keeps it open --
    // matches place_owner_hovercard_controller.js#cardMouseEnter/Leave.
    card.addEventListener("mouseenter", () => this._clearTimeouts())
    card.addEventListener("mouseleave", () => this.hide())

    document.body.appendChild(card)
    this._card = card
    this._position()
  }

  _position() {
    if (!this._card) return

    const triggerRect = this.element.getBoundingClientRect()
    const cardHeight = this._card.offsetHeight
    const cardWidth = this._card.offsetWidth

    const spaceAbove = triggerRect.top
    const top = spaceAbove >= cardHeight + 10
      ? triggerRect.top - cardHeight - 8
      : triggerRect.bottom + 8

    let left = triggerRect.left
    if (left + cardWidth > window.innerWidth - 10) {
      left = window.innerWidth - cardWidth - 10
    }

    this._card.style.top = `${Math.max(10, top)}px`
    this._card.style.left = `${Math.max(10, left)}px`
  }

  _removeCard() {
    if (this._card) this._card.remove()
    this._card = null
  }

  _clearTimeouts() {
    if (this._showTimeout) {
      clearTimeout(this._showTimeout)
      this._showTimeout = null
    }
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout)
      this._hideTimeout = null
    }
  }
}
